import { useState, useEffect, useCallback } from 'react'

declare global {
  interface Window {
    __swRegistration?: Promise<ServiceWorkerRegistration>
  }
}

function onNewWorkerWaiting(
  registration: ServiceWorkerRegistration,
  callback: (sw: ServiceWorker) => void
) {
  // Already a waiting worker
  if (registration.waiting) {
    callback(registration.waiting)
    return
  }

  registration.addEventListener('updatefound', () => {
    const newWorker = registration.installing
    if (!newWorker) return

    newWorker.addEventListener('statechange', () => {
      if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
        callback(newWorker)
      }
    })
  })
}

export function useServiceWorkerUpdate() {
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null)

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    navigator.serviceWorker.ready.then((registration) => {
      onNewWorkerWaiting(registration, setWaitingWorker)

      // Check for updates periodically (every 60s)
      const interval = setInterval(() => {
        registration.update()
      }, 60_000)

      return () => clearInterval(interval)
    })

    const regPromise = window.__swRegistration
    if (regPromise) {
      regPromise.then((registration) => {
        onNewWorkerWaiting(registration, setWaitingWorker)
        registration.update()
      })
    }
  }, [])

  // Check for updates on visibility change
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && 'serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((reg) => reg.update())
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [])

  const update = useCallback(() => {
    if (!waitingWorker) return
    // Mark that we just applied an update so the prompt doesn't flash after reload
    sessionStorage.setItem('sw-just-updated', Date.now().toString())
    let reloading = false
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return
      reloading = true
      window.location.reload()
    }, { once: true })
    waitingWorker.postMessage('SKIP_WAITING')
    setTimeout(() => {
      if (!reloading) {
        window.location.reload()
      }
    }, 2000)
  }, [waitingWorker])

  // Don't show update prompt right after an update was applied
  const justUpdated = (() => {
    const ts = sessionStorage.getItem('sw-just-updated')
    if (!ts) return false
    const elapsed = Date.now() - Number(ts)
    if (elapsed < 10_000) return true
    // Expired — clean up
    sessionStorage.removeItem('sw-just-updated')
    return false
  })()

  return { updateAvailable: !!waitingWorker && !justUpdated, update }
}
